import { useCallback, useEffect, useState } from 'react';
import type { ChangeEvent, DragEvent, MouseEvent as ReactMouseEvent } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGripVertical, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import Input from '../../form/Input';
import BigButton from '../../button/BigButton';
import FileUpload from '../../form/FileUpload';
import apiClient from '../../../service/apiClient';
import {
  useConsultOrderCodeStore,
  IConsultOrderCode,
  IConsultOrderCodeCreateRequest,
} from '../../../store/useConsultOrderCode';
import { useFranchiseStore } from '../../../store/useFranchise';
import { getFranchiseMailOrderNumber } from '../../../util/franchise';
import LandingBusinessFooter from '../common/LandingBusinessFooter';
import '../fortuneLab/CodeModal.css';

interface CodeModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  code?: IConsultOrderCode | null;
  onClose: () => void;
  onSuccess?: () => Promise<void> | void;
}

interface FormState {
  campaignName: string;
  showFooter: boolean;
  topImageUrls: string[];
  bottomImageUrls: string[];
  // 픽셀 ID
  facebookPixelId: string;
  kakaoPixelId: string;
  googleAdsPixelId: string;
  googleAdsEventSnippet: string;
  daangnPixelId: string;
  tiktokPixelId: string;
  // 버튼명
  consultButtonName: string;
  // 탭 타이틀
  documentTitle: string;
}

const extractFileName = (assetUrl: string) => {
  try {
    const decoded = decodeURIComponent(assetUrl);
    const lastSegment = decoded.split('?')[0]?.split('/').pop();
    return lastSegment && lastSegment.trim().length > 0 ? lastSegment : decoded;
  } catch (_) {
    const fallback = assetUrl.split('?')[0];
    const lastSegment = fallback.split('/').pop();
    return lastSegment && lastSegment.trim().length > 0 ? lastSegment : assetUrl;
  }
};

const buildInitialForm = (code: IConsultOrderCode | null | undefined): FormState => {
  return {
    campaignName: code?.campaignName ?? '',
    showFooter: code?.showFooter ?? true,
    topImageUrls: code?.topImageUrls ?? [],
    bottomImageUrls: code?.bottomImageUrls ?? [],
    facebookPixelId: code?.facebookPixelId ?? '',
    kakaoPixelId: code?.kakaoPixelId ?? '',
    googleAdsPixelId: code?.googleAdsPixelId ?? '',
    googleAdsEventSnippet: code?.googleAdsEventSnippet ?? '',
    daangnPixelId: code?.daangnPixelId ?? '',
    tiktokPixelId: code?.tiktokPixelId ?? '',
    consultButtonName: code?.consultButtonName ?? '',
    documentTitle: code?.documentTitle ?? '',
  };
};

const hasAnyPixel = (code: IConsultOrderCode | null | undefined): boolean => {
  if (!code) return false;
  return !!(
    code.facebookPixelId ||
    code.kakaoPixelId ||
    code.googleAdsPixelId ||
    code.googleAdsEventSnippet ||
    code.daangnPixelId ||
    code.tiktokPixelId
  );
};

const CodeModal = ({ isOpen, mode, code, onClose, onSuccess }: CodeModalProps) => {
  const { createCode, updateCode } = useConsultOrderCodeStore();
  const myfranchise = useFranchiseStore(state => state.myfranchise);
  const getMyFranchise = useFranchiseStore(state => state.getMyFranchise);
  const [form, setForm] = useState<FormState>(() => buildInitialForm(code));
  const [pendingTopFiles, setPendingTopFiles] = useState<File[]>([]);
  const [pendingBottomFiles, setPendingBottomFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragTarget, setDragTarget] = useState<'top' | 'bottom' | null>(null);
  const [showPixelSettings, setShowPixelSettings] = useState(() => hasAnyPixel(code));
  const [showAlimtalkPreview, setShowAlimtalkPreview] = useState(false);

  const resetState = useCallback(() => {
    setForm(buildInitialForm(code));
    setPendingTopFiles([]);
    setPendingBottomFiles([]);
    setUploading(false);
    setSaving(false);
    setDragIndex(null);
    setDragTarget(null);
    setShowPixelSettings(hasAnyPixel(code));
  }, [code]);

  const handleClose = useCallback(() => {
    resetState();
    onClose();
  }, [onClose, resetState]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    getMyFranchise();
  }, [getMyFranchise, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const listener = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [handleClose, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    resetState();
  }, [code, isOpen, resetState]);

  // 상단 이미지 업로드
  useEffect(() => {
    if (pendingTopFiles.length === 0) {
      return;
    }

    let isCancelled = false;

    const uploadAssets = async () => {
      setUploading(true);
      const uploadedUrls: string[] = [];

      try {
        for (const file of pendingTopFiles) {
          const formData = new FormData();
          formData.append('file', file);
          const response = await apiClient.post('/upload-file/upload', formData);
          const fileUrl = response.data?.fileFullUrl ?? '';
          if (fileUrl) {
            uploadedUrls.push(fileUrl);
          }
        }

        if (!isCancelled && uploadedUrls.length > 0) {
          setForm(prev => ({
            ...prev,
            topImageUrls: [...prev.topImageUrls, ...uploadedUrls],
          }));
        }
      } catch (error) {
        console.error('상단 이미지 업로드 실패', error);
        if (!isCancelled) {
          toast.error('상단 이미지 업로드에 실패했습니다.');
        }
      } finally {
        if (!isCancelled) {
          setUploading(false);
          setPendingTopFiles([]);
        }
      }
    };

    uploadAssets();

    return () => {
      isCancelled = true;
    };
  }, [pendingTopFiles]);

  // 하단 이미지 업로드
  useEffect(() => {
    if (pendingBottomFiles.length === 0) {
      return;
    }

    let isCancelled = false;

    const uploadAssets = async () => {
      setUploading(true);
      const uploadedUrls: string[] = [];

      try {
        for (const file of pendingBottomFiles) {
          const formData = new FormData();
          formData.append('file', file);
          const response = await apiClient.post('/upload-file/upload', formData);
          const fileUrl = response.data?.fileFullUrl ?? '';
          if (fileUrl) {
            uploadedUrls.push(fileUrl);
          }
        }

        if (!isCancelled && uploadedUrls.length > 0) {
          setForm(prev => ({
            ...prev,
            bottomImageUrls: [...prev.bottomImageUrls, ...uploadedUrls],
          }));
        }
      } catch (error) {
        console.error('하단 이미지 업로드 실패', error);
        if (!isCancelled) {
          toast.error('하단 이미지 업로드에 실패했습니다.');
        }
      } finally {
        if (!isCancelled) {
          setUploading(false);
          setPendingBottomFiles([]);
        }
      }
    };

    uploadAssets();

    return () => {
      isCancelled = true;
    };
  }, [pendingBottomFiles]);

  const handleInputChange = (field: keyof FormState) => (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleRemoveAsset = useCallback((target: 'top' | 'bottom', index: number) => (event: ReactMouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    const field = target === 'top' ? 'topImageUrls' : 'bottomImageUrls';
    setForm(prev => ({
      ...prev,
      [field]: prev[field].filter((_, assetIndex) => assetIndex !== index),
    }));
  }, []);

  const handleDragStart = useCallback((target: 'top' | 'bottom', index: number) => (event: DragEvent<HTMLDivElement>) => {
    setDragIndex(index);
    setDragTarget(target);
    event.dataTransfer.setData('text/plain', String(index));
    event.dataTransfer.effectAllowed = 'move';
  }, []);

  const handleDragOver = useCallback((event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop = useCallback((target: 'top' | 'bottom', index: number) => (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (dragIndex === null || dragIndex === index || dragTarget !== target) {
      setDragIndex(null);
      setDragTarget(null);
      return;
    }

    const field = target === 'top' ? 'topImageUrls' : 'bottomImageUrls';
    setForm(prev => {
      const nextAssets = [...prev[field]];
      const [moved] = nextAssets.splice(dragIndex, 1);
      nextAssets.splice(index, 0, moved);
      return {
        ...prev,
        [field]: nextAssets,
      };
    });
    setDragIndex(null);
    setDragTarget(null);
  }, [dragIndex, dragTarget]);

  const handleDragEnd = useCallback(() => {
    setDragIndex(null);
    setDragTarget(null);
  }, []);

  const handleSubmit = useCallback(async (event?: ReactMouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    if (saving || uploading) {
      return;
    }

    if (!form.campaignName.trim()) {
      toast.error('캠페인명을 입력해주세요.');
      return;
    }
    const topImageUrls = form.topImageUrls.map(asset => asset.trim()).filter(Boolean);
    const bottomImageUrls = form.bottomImageUrls.map(asset => asset.trim()).filter(Boolean);

    const payload: IConsultOrderCodeCreateRequest = {
      campaignName: form.campaignName.trim(),
      showFooter: form.showFooter,
      topImageUrls: topImageUrls.length > 0 ? topImageUrls : undefined,
      bottomImageUrls: bottomImageUrls.length > 0 ? bottomImageUrls : undefined,
      facebookPixelId: form.facebookPixelId.trim() || undefined,
      kakaoPixelId: form.kakaoPixelId.trim() || undefined,
      googleAdsPixelId: form.googleAdsPixelId.trim() || undefined,
      googleAdsEventSnippet: form.googleAdsEventSnippet.trim() || undefined,
      daangnPixelId: form.daangnPixelId.trim() || undefined,
      tiktokPixelId: form.tiktokPixelId.trim() || undefined,
      consultButtonName: form.consultButtonName.trim() || undefined,
      documentTitle: form.documentTitle.trim() || undefined,
    };

    setSaving(true);
    try {
      if (mode === 'create') {
        await createCode(payload);
        handleClose();
      } else if (code) {
        const success = await updateCode(code.id, payload);
        if (success) {
          if (onSuccess) {
            await onSuccess();
          }
          handleClose();
        }
      }
    } finally {
      setSaving(false);
    }
  }, [code, createCode, updateCode, form, handleClose, mode, onSuccess, saving, uploading]);

  const hasTopImages = form.topImageUrls.length > 0;
  const hasBottomImages = form.bottomImageUrls.length > 0;

  if (!isOpen) {
    return null;
  }

  const heading = mode === 'create' ? '캠페인 코드 생성' : '캠페인 코드 수정';
  const submitLabel = mode === 'create' ? (saving ? '생성 중...' : '생성') : (saving ? '수정 중...' : '수정');

  const renderAssetList = (assets: string[], target: 'top' | 'bottom') => (
    <div className="landing-assets-list">
      {assets.map((asset, index) => (
        <div
          key={`${asset}-${index}`}
          className="landing-asset-card"
          draggable
          onDragStart={handleDragStart(target, index)}
          onDragOver={handleDragOver}
          onDrop={handleDrop(target, index)}
          onDragEnd={handleDragEnd}
        >
          <button
            type="button"
            className="landing-asset-drag"
            aria-label="순서 변경"
            disabled={uploading || saving}
          >
            <FontAwesomeIcon icon={faGripVertical} />
          </button>
          <div className="landing-asset-info">
            <span className="landing-asset-index">#{index + 1}</span>
            <span className="landing-asset-name">{extractFileName(asset)}</span>
          </div>
          <button
            type="button"
            className="landing-asset-remove"
            onClick={handleRemoveAsset(target, index)}
            disabled={saving || uploading}
          >
            <FontAwesomeIcon icon={faTrash} />
            제거
          </button>
        </div>
      ))}
    </div>
  );

  return (
    <div className="fortunelab_modal_overlay">
      <div
        className="fortunelab_modal fortunelab_modal--wide"
        onClick={event => event.stopPropagation()}
      >
        <div className="fortunelab_modal_header">
          <h2>{heading}</h2>
          <button className="fortunelab_modal_close" onClick={handleClose} style={{ fontSize: '35px', padding: '8px 12px' }}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="fortunelab_modal_body fortunelab_modal_body--grid">
          <div className="form-section">
            <div className="form-field">
              <label>캠페인명 *</label>
              <Input
                value={form.campaignName}
                onChange={handleInputChange('campaignName')}
                placeholder="캠페인명을 입력하세요"
                width="100%"
                textAlign="left"
                disabled={saving}
              />
            </div>

            <div className="form-field">
              <label>상담 버튼명 (선택)</label>
              <p className="form-field-desc">랜딩페이지의 상담 신청 버튼에 표시될 텍스트입니다. 기본값: 카카오톡 상담 신청하기 (최대 20자)</p>
              <Input
                value={form.consultButtonName}
                onChange={handleInputChange('consultButtonName')}
                placeholder="카카오톡 상담 신청하기"
                width="100%"
                textAlign="left"
                disabled={saving}
                maxLength={20}
              />
            </div>

            <div className="form-field">
              <label>탭 타이틀 (선택)</label>
              <p className="form-field-desc">브라우저 탭에 표시될 제목입니다. 기본값: 상담 신청 (최대 50자)</p>
              <Input
                value={form.documentTitle}
                onChange={handleInputChange('documentTitle')}
                placeholder="상담 신청"
                width="100%"
                textAlign="left"
                disabled={saving}
                maxLength={50}
              />
            </div>

            <div className="form-field">
              <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <div>
                  <span>푸터 표시</span>
                  <p className="form-field-desc" style={{ marginTop: '4px', marginBottom: 0 }}>
                    랜딩페이지 하단의 사업자 정보 푸터를 노출합니다.
                  </p>
                </div>
                <div
                  style={{
                    position: 'relative',
                    width: '44px',
                    height: '24px',
                    backgroundColor: form.showFooter ? '#4F46E5' : '#D1D5DB',
                    borderRadius: '12px',
                    transition: 'background-color 0.2s',
                    opacity: saving ? 0.5 : 1,
                    flexShrink: 0,
                    cursor: saving ? 'not-allowed' : 'pointer',
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    if (!saving) {
                      setForm(prev => ({ ...prev, showFooter: !prev.showFooter }));
                    }
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: '2px',
                      left: form.showFooter ? '22px' : '2px',
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#fff',
                      borderRadius: '50%',
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }}
                  />
                </div>
              </label>
            </div>

            <div className="form-field">
              <label>상단 이미지 (선택)</label>
              <p className="form-field-desc">입력폼 위에 표시되는 이미지입니다.</p>
              <FileUpload
                setFiles={setPendingTopFiles}
                accept="image/*"
                multiple
                note="이미지 또는 GIF, 최대 20MB"
              />
              {hasTopImages && renderAssetList(form.topImageUrls, 'top')}
            </div>

            <div className="form-field">
              <label>하단 이미지 (선택)</label>
              <p className="form-field-desc">입력폼 아래에 표시되는 이미지입니다.</p>
              <FileUpload
                setFiles={setPendingBottomFiles}
                accept="image/*"
                multiple
                note="이미지 또는 GIF, 최대 20MB"
              />
              {hasBottomImages && renderAssetList(form.bottomImageUrls, 'bottom')}
            </div>

            <div className="form-field">
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                <span>픽셀 사용(선택)</span>
                <div
                  style={{
                    position: 'relative',
                    width: '44px',
                    height: '24px',
                    backgroundColor: showPixelSettings ? '#4F46E5' : '#D1D5DB',
                    borderRadius: '12px',
                    transition: 'background-color 0.2s',
                    opacity: saving ? 0.5 : 1,
                  }}
                  onClick={(e) => {
                    e.preventDefault();
                    if (!saving) setShowPixelSettings(!showPixelSettings);
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: '2px',
                      left: showPixelSettings ? '22px' : '2px',
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#fff',
                      borderRadius: '50%',
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }}
                  />
                </div>
              </label>

              {showPixelSettings && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '12px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ minWidth: '100px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Facebook</span>
                    <Input
                      value={form.facebookPixelId}
                      onChange={handleInputChange('facebookPixelId')}
                      placeholder="픽셀 ID"
                      width="100%"
                      textAlign="left"
                      disabled={saving}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ minWidth: '100px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>카카오</span>
                    <Input
                      value={form.kakaoPixelId}
                      onChange={handleInputChange('kakaoPixelId')}
                      placeholder="픽셀 ID"
                      width="100%"
                      textAlign="left"
                      disabled={saving}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ minWidth: '100px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>Google Ads</span>
                    <Input
                      value={form.googleAdsPixelId}
                      onChange={handleInputChange('googleAdsPixelId')}
                      placeholder="픽셀 ID"
                      width="100%"
                      textAlign="left"
                      disabled={saving}
                    />
                    <Input
                      value={form.googleAdsEventSnippet}
                      onChange={handleInputChange('googleAdsEventSnippet')}
                      placeholder="이벤트 스니펫 (전환측정용)"
                      width="100%"
                      textAlign="left"
                      disabled={saving}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ minWidth: '100px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>당근</span>
                    <Input
                      value={form.daangnPixelId}
                      onChange={handleInputChange('daangnPixelId')}
                      placeholder="픽셀 ID"
                      width="100%"
                      textAlign="left"
                      disabled={saving}
                    />
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ minWidth: '100px', fontSize: '14px', fontWeight: '600', color: '#374151' }}>TikTok</span>
                    <Input
                      value={form.tiktokPixelId}
                      onChange={handleInputChange('tiktokPixelId')}
                      placeholder="픽셀 ID"
                      width="100%"
                      textAlign="left"
                      disabled={saving}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="preview-column">
            <section className="preview-panel">
              <h3>랜딩 미리보기</h3>
              <div className="landing-preview">
                <div className="landing-preview__content">
                  {/* 상단 이미지 */}
                  <div className="landing-preview__media">
                    {hasTopImages ? (
                      form.topImageUrls.map((asset, index) => (
                        <img key={`top-${asset}-${index}`} src={asset} alt={`상단 이미지 ${index + 1}`} />
                      ))
                    ) : (
                      <div className="landing-preview__placeholder">
                        상단 이미지를 업로드하면 이곳에 표시됩니다.
                      </div>
                    )}
                  </div>

                  {/* 입력폼 */}
                  <form
                    className="fortune_lab_form landing-preview__form"
                    onSubmit={event => event.preventDefault()}
                  >
                    <input
                      type="text"
                      placeholder="이름"
                      autoComplete="name"
                      maxLength={20}
                      value=""
                      disabled
                    />
                    <input
                      type="tel"
                      placeholder="전화번호 (숫자만 입력)"
                      autoComplete="tel"
                      inputMode="numeric"
                      maxLength={11}
                      value=""
                      disabled
                    />

                    {/*<input*/}
                    {/*  type="text"*/}
                    {/*  placeholder="생년월일 (yyyy.mm.dd)"*/}
                    {/*  autoComplete="bday"*/}
                    {/*  inputMode="numeric"*/}
                    {/*  value=""*/}
                    {/*  disabled*/}
                    {/*/>*/}

                    {/*<input*/}
                    {/*  type="text"*/}
                    {/*  placeholder="생시 (예: 14:30, 선택사항)"*/}
                    {/*  inputMode="numeric"*/}
                    {/*  value=""*/}
                    {/*  disabled*/}
                    {/*/>*/}

                    {/*<select*/}
                    {/*  disabled*/}
                    {/*  style={{*/}
                    {/*    width: '100%',*/}
                    {/*    height: '52px',*/}
                    {/*    border: '1px solid #dddddd',*/}
                    {/*    borderRadius: '8px',*/}
                    {/*    padding: '0 40px 0 16px',*/}
                    {/*    fontSize: '16px',*/}
                    {/*    outline: 'none',*/}
                    {/*    backgroundColor: '#fff',*/}
                    {/*    appearance: 'none',*/}
                    {/*    WebkitAppearance: 'none',*/}
                    {/*    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,*/}
                    {/*    backgroundRepeat: 'no-repeat',*/}
                    {/*    backgroundPosition: 'right 16px center',*/}
                    {/*  }}*/}
                    {/*>*/}
                    {/*  <option value="SOLAR">양력</option>*/}
                    {/*  <option value="LUNAR">음력</option>*/}
                    {/*  <option value="LUNAR_INTER">음력 (윤달)</option>*/}
                    {/*</select>*/}

                    {/*<select*/}
                    {/*  disabled*/}
                    {/*  style={{*/}
                    {/*    width: '100%',*/}
                    {/*    height: '52px',*/}
                    {/*    border: '1px solid #dddddd',*/}
                    {/*    borderRadius: '8px',*/}
                    {/*    padding: '0 40px 0 16px',*/}
                    {/*    fontSize: '16px',*/}
                    {/*    outline: 'none',*/}
                    {/*    backgroundColor: '#fff',*/}
                    {/*    appearance: 'none',*/}
                    {/*    WebkitAppearance: 'none',*/}
                    {/*    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,*/}
                    {/*    backgroundRepeat: 'no-repeat',*/}
                    {/*    backgroundPosition: 'right 16px center',*/}
                    {/*  }}*/}
                    {/*>*/}
                    {/*  <option value="M">남성</option>*/}
                    {/*  <option value="F">여성</option>*/}
                    {/*</select>*/}

                    <div className="privacy_agreement">
                      <label className="privacy_agreement_checkbox">
                        <input
                            type="checkbox"
                            disabled
                        />
                        <span className="privacy_agreement_text">
                        개인정보 수집·이용에 동의합니다
                        <button
                            type="button"
                            className="privacy_link_button privacy_agreement_link"
                            disabled
                        >
                          개인정보 처리방침
                        </button>
                      </span>
                      </label>
                      <label className="privacy_agreement_checkbox">
                        <input
                            disabled
                            type="checkbox"
                        />
                        <span className="privacy_agreement_text">
                          제3자 제공에 동의합니다
                          <button
                              type="button"
                              className="privacy_link_button privacy_agreement_link"
                              disabled
                          >
                            자세히 보기
                          </button>
                        </span>
                      </label>
                    </div>

                    <button type="button" className="kakao_button" disabled>
                      {form.consultButtonName.trim() || '카카오톡 상담 신청하기'}
                    </button>
                  </form>

                  {/* 하단 이미지 */}
                  {hasBottomImages && (
                    <div className="landing-preview__media landing-preview__media--bottom">
                      {form.bottomImageUrls.map((asset, index) => (
                        <img key={`bottom-${asset}-${index}`} src={asset} alt={`하단 이미지 ${index + 1}`} />
                      ))}
                    </div>
                  )}

                  {form.showFooter && (
                    <LandingBusinessFooter
                      companyNumber={myfranchise?.companyNumber}
                      mailOrderNumber={getFranchiseMailOrderNumber(myfranchise)}
                      companyName={myfranchise?.companyName}
                      ceo={myfranchise?.ceo}
                      serviceName={myfranchise?.bizProfileName}
                    />
                  )}
                </div>
              </div>
            </section>

            <section className="preview-panel">
              <h3
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  cursor: 'pointer',
                  userSelect: 'none',
                }}
                onClick={() => setShowAlimtalkPreview(!showAlimtalkPreview)}
              >
                <span>상담 알림톡 미리보기</span>
                <div
                  style={{
                    position: 'relative',
                    width: '44px',
                    height: '24px',
                    backgroundColor: showAlimtalkPreview ? '#4F46E5' : '#D1D5DB',
                    borderRadius: '12px',
                    transition: 'background-color 0.2s',
                  }}
                >
                  <div
                    style={{
                      position: 'absolute',
                      top: '2px',
                      left: showAlimtalkPreview ? '22px' : '2px',
                      width: '20px',
                      height: '20px',
                      backgroundColor: '#fff',
                      borderRadius: '50%',
                      transition: 'left 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    }}
                  />
                </div>
              </h3>
              {showAlimtalkPreview && (
                <div className="alimtalk-preview">
                  <div className="alimtalk-header">사주톡톡</div>
                  <div className="alimtalk-body">
                    <p>안녕하세요. 고객님😁</p>
                    <p>사주풀이 신청이 완료됐어요.</p>
                    <br />
                    <p>웃고 넘길 고민도,</p>
                    <p>혼자 끙끙 앓아온</p>
                    <p>고민도 괜찮습니다 😌</p>
                    <br />
                    <p>사주를 통해 지금 흐름과</p>
                    <p>해답을 찾아드릴게요 🔑</p>
                    <br />
                    <p>사주풀이엔 복비가 발생합니다.</p>
                    <br />
                    <p>고민을 남겨주시면</p>
                    <p>사주풀이에 필요한 복비(풀이 비용)</p>
                    <p>함께 안내드릴게요.</p>
                    <br />
                    <p>💬편하게 고민을</p>
                    <p>채팅방에 남겨주세요</p>
                  </div>
                  <div className="alimtalk-footer">
                    <button
                        type="button"
                        className="alimtalk-button"
                    >
                      채널 추가
                    </button>
                  </div>
                </div>
              )}
            </section>
          </div>
        </div>

        <div className="fortunelab_modal_footer">
          <BigButton
            type="submit"
            text={submitLabel}
            onClick={handleSubmit}
            style={{ flex: '0 0 50%' }}
            disabled={saving || uploading}
          />
        </div>
      </div>
    </div>
  );
};

export default CodeModal;
